/* Tiny client-side store for the customer's favorite restaurant IDs.
 * Used by every heart button on the site so the favorited state stays
 * in sync across all cards/pages without each component re-querying
 * `GET /api/customer/favorites` on its own.
 *
 * Public surface:
 *   • hydrate(token)        — fetch favorites from the server (idempotent)
 *   • isFavorited(rid)      — synchronous lookup against the in-memory set
 *   • setFavorited(rid, on) — local override (called after a toggle POST)
 *   • subscribe(fn)         — pub/sub for React components
 *   • clear()               — wipe the store on logout
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVT = 'nofee:favorites:changed';

const state = {
  ids: new Set(),
  hydrated: false,
  hydrating: false,
};

const notify = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EVT));
  }
};

export const subscribe = (fn) => {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(EVT, fn);
  return () => window.removeEventListener(EVT, fn);
};

export const isFavorited = (restaurantId) => state.ids.has(restaurantId);

export const getFavoriteIds = () => Array.from(state.ids);

export const setFavorited = (restaurantId, on) => {
  const before = state.ids.size;
  if (on) state.ids.add(restaurantId);
  else state.ids.delete(restaurantId);
  if (state.ids.size !== before || on !== state.ids.has(restaurantId)) {
    notify();
  } else {
    notify();
  }
};

export const clear = () => {
  state.ids.clear();
  state.hydrated = false;
  notify();
};

export const hydrate = async (token) => {
  if (!token || state.hydrating || state.hydrated) return;
  state.hydrating = true;
  try {
    const r = await axios.get(`${API}/customer/favorites`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const favs = r?.data?.favorites || [];
    state.ids = new Set(favs.map((f) => f.restaurant_id).filter(Boolean));
    state.hydrated = true;
    notify();
  } catch {
    // Silent — heart UI just stays in its default un-favorited state.
  } finally {
    state.hydrating = false;
  }
};
